import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  FormTitle,
  Input,
  Radio,
  ShowInput,
  Steps,
  SubContent,
  Tag,
  Tooltip,
  Wrapper,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apiV2EnergyConsumptionPlanApprovalDelayEnergyConsumptionPost,
  apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPost,
  V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import { Space } from 'antd';
import dayjs from 'dayjs';
import { ApprovalStatus, ApprovalStatusColorMap, ApprovalStatusMap } from './util';
import { isEmpty } from 'lodash-es';
import actualApprovalImg from './imgs/actualApproval@2x.png';
import unActualApprovalImg from './imgs/unActualApproval@2x.png';

export type DelayEnergyConsumptionDetail = Exclude<
  V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse['delayEnergyConsumptionDetailList'],
  undefined
>[number];

export type ApprovalDetail = V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse['firstApprovalDetail'];

const EnergyDeferredApplicationApproval = ({ isEdit }: { isEdit?: boolean }) => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [form] = Form.useForm();
  const [detail, setDetail] = useState<V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse>({});
  const [current, setCurrent] = useState<number>(0);

  useEffect(() => {
    apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPost({
      id,
    }).then(res => {
      if (res) {
        setDetail(res);
        if (res.firstApprovalDetail?.approvalResult === ApprovalStatus.PENDING) {
          setCurrent(0);
        } else if (res.secondApprovalDetail?.approvalResult === ApprovalStatus.PENDING) {
          setCurrent(1);
        } else {
          setCurrent(3);
        }
      }
    });
  }, []);

  const onFinish = () => {
    form.validateFields().then(() => {
      const params = {
        id,
        ...form.getFieldsValue(true),
        level: current,
      };

      apiV2EnergyConsumptionPlanApprovalDelayEnergyConsumptionPost(params).then(() => navigate(-1));
    });
  };

  const ApprovalRender = (approvalData: ApprovalDetail, level: string, isEdit?: boolean) => (
    <div className={styles.approvalContainer}>
      <div className={styles.approvalInfo}>
        <Space size={74} className={styles.approvalRow}>
          <div>
            <span className={styles.label}>{level}人：</span>
            {(approvalData?.staffList ?? []).map((staff, index) => {
              return (
                <Space size={4}>
                  <Tooltip title={staff.isActualApproval ? '实际审批人' : undefined}>
                    <img
                      src={staff.isActualApproval ? actualApprovalImg : unActualApprovalImg}
                      alt="审批人"
                      width={16}
                      style={{ marginTop: '-2px' }}
                    />
                  </Tooltip>

                  {staff.staffName}
                  {approvalData!.staffList!.length > index + 1 ? '、' : ''}
                </Space>
              );
            })}
          </div>
          {approvalData?.approvalResult !== ApprovalStatus.PENDING && (
            <>
              <div style={{ width: 260 }}>
                <span className={styles.label}>{level}时间：</span>
                {approvalData?.approvalTime ? dayjs(approvalData?.approvalTime).format('YYYY-MM-DD HH:mm:ss') : '--'}
              </div>
              <div style={{ width: 140 }}>
                <span className={styles.label}>审批结果：</span>
                {approvalData?.approvalResult === ApprovalStatus.NOT_ARRIVED ? (
                  '--'
                ) : (
                  <Tag border="solid" color={ApprovalStatusColorMap[approvalData?.approvalResult as ApprovalStatus]}>
                    {ApprovalStatusMap[approvalData?.approvalResult as ApprovalStatus]}
                  </Tag>
                )}
              </div>
              <div>
                <span className={styles.label}>备注：</span>
                {approvalData?.remark || '无'}
              </div>
            </>
          )}
        </Space>
        {isEdit && approvalData?.approvable && approvalData?.approvalResult === ApprovalStatus.PENDING && (
          <div className={styles.formItemStyle}>
            <Form.Item
              label="审批结果"
              rules={[{ required: true, message: '请选择审批结果' }]}
              name={['approvalDetail', 'approvalResult']}
            >
              <Radio.Group>
                <Radio value={ApprovalStatus.APPROVED}>{ApprovalStatusMap[ApprovalStatus.APPROVED]}</Radio>
                <Radio value={ApprovalStatus.REJECTED}>{ApprovalStatusMap[ApprovalStatus.REJECTED]}</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item label="备注" name={['approvalDetail', 'remark']}>
              <Input.TextArea rows={4} style={{ width: 400 }} />
            </Form.Item>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes}>
      <FormTitle title={isEdit ? '审批' : '查看'} />
      <Form form={form} isOrigin={true}>
        <SubContent title="基本信息">
          <Form.Item label="申请部门">
            <ShowInput value={detail.ownerOrgName ?? '-'} />
          </Form.Item>
        </SubContent>
        <SubContent title="申请明细">
          <div className={styles.cardContainer}>
            {detail?.delayEnergyConsumptionDetailList?.map((item, index) => (
              <div className={styles.card} key={index}>
                <div className={styles.title}>
                  <span className={styles.label}>明细{index + 1}</span>
                </div>
                <div className={styles.content}>
                  <Form.Item label="区域选择">{item.areaList?.map(i => i.zoneName)?.join('; ')}</Form.Item>
                  <Form.Item label="作业人数">{item.workers ?? '--'}</Form.Item>
                  <Form.Item label="作业内容">{item.content}</Form.Item>
                  <Form.Item label="用能类型">{item.mediumList?.map(i => i.mediumName)?.join('; ') ?? '--'}</Form.Item>
                  <Form.Item label="开始/结束时间">
                    {item.startTime ? dayjs(item.startTime).format('YYYY-MM-DD HH:mm') : '-'} ~{' '}
                    {item.endTime ? dayjs(item.endTime).format('YYYY-MM-DD HH:mm') : '--'}
                  </Form.Item>
                  <Form.Item label="用能时长">{item.duration ?? '--'}小时</Form.Item>
                </div>
              </div>
            ))}
          </div>
        </SubContent>
        <SubContent title="审批情况">
          <Steps direction="vertical" size="small" current={current} className={styles.approvalSteps}>
            {!isEmpty(detail.firstApprovalDetail) && (
              <Steps.Step
                title="一级审批"
                description={ApprovalRender(detail.firstApprovalDetail, '一级审批', isEdit)}
              />
            )}
            {!isEmpty(detail.secondApprovalDetail) && (
              <Steps.Step
                title="二级审批"
                description={ApprovalRender(detail.secondApprovalDetail, '二级审批', isEdit)}
              />
            )}
            {!isEdit && detail.ccStaffIdList && (
              <Steps.Step
                title={
                  <>
                    <div style={{ width: 56 }}>抄送人</div>
                    {detail.secondApprovalDetail?.approvalResult === ApprovalStatus.APPROVED && (
                      <div style={{ fontSize: '12px', color: 'var(--mx-text-secondary-color))' }}>已抄送</div>
                    )}
                  </>
                }
                description={
                  <div className={styles.approvalContainer}>
                    <div className={styles.approvalInfo}>
                      <Space size={16} className={styles.approvalRow} style={{ marginBottom: 0 }}>
                        <div>
                          <span className={styles.label}>抄送人：</span>
                          <span>{detail.ccStaffIdList.map(i => i.staffName)?.join('、')}</span>
                        </div>
                      </Space>
                    </div>
                  </div>
                }
              />
            )}
          </Steps>
        </SubContent>
        <Space size={8}>
          {isEdit && (
            <Button type="primary" onClick={onFinish}>
              确定
            </Button>
          )}
          <Button onClick={() => navigate(-1)}>返回</Button>
          {detail.approvable && (
            <span style={{ color: 'var(--mx-text-desc-color)', marginLeft: 8 }}>
              前往
              <Button
                type="link"
                style={{ padding: '0 4px' }}
                onClick={() => navigate('/energy/usage/application?tabs=1')}
              >
                批量审批列表
              </Button>
              选择延时用能申请单进行批量审批
            </span>
          )}
        </Space>
      </Form>
    </Wrapper>
  );
};

export default EnergyDeferredApplicationApproval;
